import React, { useEffect, useMemo, useState } from 'react';
import PageHeader from '../components/PageHeader';
import Card from '../components/Card';
import SearchInput from '../components/SearchInput';
import DataTable, { Column } from '../components/DataTable';
import Badge, { statusToTone } from '../components/Badge';
import SidePanel from '../components/SidePanel';
import Button from '../components/Button';
import Toast, { ToastMessage } from '../components/Toast';
import Pagination from '../components/Pagination';
import axios from 'axios';
import { BaseUrl } from '../services/adminApi';


interface Ticket {
  id: number | string;
  ticket_id: number | string;
  ticket_number: string;
  user_id?: number | string;
  name: string;
  email: string;
  subject: string;
  message: string;
  category: string;
  priority: 'Low' | 'Medium' | 'High' | string;
  status: string;
  source?: string;
  admin_reply?: string;
  internal_notes?: string;
  created_at: string;
  updated_at: string;
  closed_at?: string | null;
  reopened_at?: string | null;
}

const STATUSES = ['Open', 'Pending', 'Resolved', 'Closed'];

const CATEGORIES = ['Billing', 'Technical', 'Account', 'Report issue', 'Feature request', 'Other'];
const SOURCES = ['Website', 'API'];
const PAGE_SIZE = 10;

function formatDate(iso: string) {
  return new Date(iso).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
}

function formatDateTime(iso: string) {
  return new Date(iso).toLocaleString('en-GB', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
}

function priorityTone(p: string) {
  if (p === 'High') return 'red';
  if (p === 'Medium') return 'amber';
  return 'gray';
}

export default function Tickets() {
  const [tickets, setTickets] = useState<Ticket[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, /* setStatusFilter */] = useState('all');
  const [priorityFilter, setPriorityFilter] = useState('all');
  const [categoryFilter, /* setCategoryFilter */] = useState('all');
  const [sourceFilter, /* setSourceFilter */] = useState('all');
  const [page, setPage] = useState(1);
  const [totalCount, setTotalCount] = useState<number | null>(null);
  const [selected, setSelected] = useState<Ticket | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [adminReply, setAdminReply] = useState('');
  const [internalNotes, setInternalNotes] = useState('');
  const [statusDraft, setStatusDraft] = useState('Open');
  const [categoryDraft, setCategoryDraft] = useState('');
  const [priorityDraft, setPriorityDraft] = useState('');
  const [activeTab, setActiveTab] = useState<'reply' | 'notes' | 'meta'>('reply');
  const [sending, setSending] = useState(false);
  const [toast, setToast] = useState<ToastMessage | null>(null);

  const showToast = (text: string, type: ToastMessage['type'] = 'success') => {
    setToast({ text, type });
    setTimeout(() => setToast(null), 3000);
  };

  useEffect(() => {
    setPage(1);
  }, [search, statusFilter, priorityFilter, categoryFilter, sourceFilter]);

  useEffect(() => {
    async function load() {
      setLoading(true);
      const params: Record<string, string | number> = {
        page_num: page,
        page_size: PAGE_SIZE,
      };
      if (statusFilter !== 'all') params.status = statusFilter;
      if (priorityFilter !== 'all') params.priority = priorityFilter;
      if (categoryFilter !== 'all') params.category = categoryFilter;
      if (sourceFilter !== 'all') params.source = sourceFilter;
      if (search) params.search = search;

      try {
        const res = await axios.get(BaseUrl + 'tickets/admin/', { params });
        const data = res.data;

        // Backend may return either a plain array or a paginated
        // { results, count } object — support both.
        let results: any[];
        let count: number;
        if (Array.isArray(data)) {
          results = data;
          count = data.length;
        } else {
          results = (data as any)?.results ?? (data as any)?.tickets ?? [];
          count = (data as any)?.count ?? (data as any)?.total ?? results.length;
        }
        const normalized: Ticket[] = (Array.isArray(results) ? results : []).map((t: any) => ({
          ...t,
          id: t.id ?? t.ticket_id,
        }));
        setTickets(normalized);
        setTotalCount(count);
      } catch {
        setTickets([]);
        setTotalCount(0);
        showToast('Could not load tickets.', 'error');
      } finally {
        setLoading(false);
      }
    }
    load();
  }, [page, statusFilter, priorityFilter, categoryFilter, sourceFilter, search]);

  const totalPages = totalCount ? Math.max(1, Math.ceil(totalCount / PAGE_SIZE)) : 1;

  const filtered = useMemo(() => {
    return tickets.filter(t => {
      const matchesSearch = !search ||
        t.subject.toLowerCase().includes(search.toLowerCase()) ||
        t.name.toLowerCase().includes(search.toLowerCase()) ||
        t.ticket_number?.toLowerCase().includes(search.toLowerCase());
      const matchesStatus = statusFilter === 'all' || t.status === statusFilter;
      const matchesPriority = priorityFilter === 'all' || t.priority === priorityFilter;
      const matchesCategory = categoryFilter === 'all' || t.category === categoryFilter;
      const matchesSource = sourceFilter === 'all' || t.source === sourceFilter;
      return matchesSearch && matchesStatus && matchesPriority && matchesCategory && matchesSource;
    });
  }, [tickets, search, statusFilter, priorityFilter, categoryFilter, sourceFilter]);

  const openTicket = async (t: Ticket) => {
    setSelected(t);
    setStatusDraft(t.status);
    setCategoryDraft(t.category || '');
    setPriorityDraft(t.priority || 'Low');
    setAdminReply(t.admin_reply || '');
    setInternalNotes(t.internal_notes || '');
    setActiveTab('reply');
    setDetailLoading(true);
    try {

      let full: any = null;
      try {
        const res = await axios.get(BaseUrl + `tickets/admin/${t.ticket_id}/`);
        full = res.data;
      } catch {
        const res = await axios.get(BaseUrl + `tickets/${t.ticket_id}/`);
        full = res.data;
      }
      if (full) {
        setSelected(prev => prev ? { ...prev, ...full, id: full.id ?? full.ticket_id } : prev);
        setStatusDraft(full.status ?? t.status);
        setCategoryDraft(full.category ?? t.category ?? '');
        setPriorityDraft(full.priority ?? t.priority ?? 'Low');
        setAdminReply(full.admin_reply || '');
        setInternalNotes(full.internal_notes || '');
      }
    } catch {
    } finally {
      setDetailLoading(false);
    }
  };

  const saveTicket = async (patch: Record<string, any>, successMsg: string) => {
    if (!selected) return;
    setSending(true);
    try {
      await axios.post(BaseUrl + `tickets/admin/${selected.ticket_id}/update/`, {
        status: statusDraft,
        admin_reply: adminReply,
        internal_notes: internalNotes,
        category: categoryDraft,
        priority: priorityDraft,
        ...patch,
      });
      setTickets(prev => prev.map(t => t.ticket_id === selected.ticket_id
        ? { ...t, status: statusDraft, admin_reply: adminReply, internal_notes: internalNotes, category: categoryDraft, priority: priorityDraft, ...patch }
        : t));
      setSelected(prev => prev ? { ...prev, status: statusDraft, admin_reply: adminReply, internal_notes: internalNotes, category: categoryDraft, priority: priorityDraft, ...patch } : prev);
      showToast(successMsg);
    } catch {
      showToast('Could not update the ticket.', 'error');
    } finally {
      setSending(false);
    }
  };

  const handleResolve = async () => {
    await saveTicket({}, 'Ticket updated.');
    setSelected(null);
    setAdminReply('');
    setInternalNotes('');
  };

  const columns: Column<Ticket>[] = [
    {
      key: 'subject', header: 'Subject', render: t => (
        <div>
          <span className="font-bold text-gray-900">{t.subject}</span>
          <div className="text-[11px] text-gray-400 font-medium mt-0.5">{t.ticket_number}</div>
        </div>
      )
    },
    { key: 'name', header: 'From', render: t => t.name },
    { key: 'category', header: 'Category', render: t => t.category || '—' },
    { key: 'priority', header: 'Priority', render: t => <Badge tone={priorityTone(t.priority)}>{t.priority}</Badge> },
    { key: 'status', header: 'Status', render: t => <Badge tone={statusToTone(t.status)}>{t.status}</Badge> },
    { key: 'date', header: 'Opened', render: t => formatDate(t.created_at), align: 'right' },
 {
  key: 'actions',
  header: 'Action',
  align: 'right',
  render: t => (
    <div onClick={(e) => e.stopPropagation()}>
      <Button
        size="sm"
        variant="secondary"
        icon="ti-message-circle"
        onClick={() => openTicket(t)}
      >
        Reply
      </Button>
    </div>
  ),
},
  ];
  return (
    <div>
      <Toast message={toast} />
      <PageHeader title="Support Tickets" subtitle="Respond to and resolve user support requests." />

      <Card>
        <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
          <SearchInput value={search} onChange={setSearch} placeholder="Search tickets…" className="w-full sm:w-64" />
          {/* <select
            value={statusFilter}
            onChange={e => setStatusFilter(e.target.value)}
            className="text-[13px] font-medium text-gray-700 bg-gray-50 border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:border-navy"
          >
            <option value="all">All statuses</option>
            <option value="Open">Open</option>
            <option value="Pending">Pending</option>
            <option value="Resolved">Resolved</option>
            <option value="Closed">Closed</option>
          </select> */}
          <select
            value={priorityFilter}
            onChange={e => setPriorityFilter(e.target.value)}
            className="text-[13px] font-medium text-gray-700 bg-gray-50 border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:border-navy"
          >
            <option value="all">All priorities</option>
            <option value="Low">Low</option>
            <option value="Medium">Medium</option>
            <option value="High">High</option>
          </select>
          {/* <select
            value={categoryFilter}
            onChange={e => setCategoryFilter(e.target.value)}
            className="text-[13px] font-medium text-gray-700 bg-gray-50 border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:border-navy"
          >
            <option value="all">All categories</option>
            {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
          </select> */}
          {/* <select
            value={sourceFilter}
            onChange={e => setSourceFilter(e.target.value)}
            className="text-[13px] font-medium text-gray-700 bg-gray-50 border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:border-navy"
          >
            <option value="all">All sources</option>
            {SOURCES.map(s => <option key={s} value={s}>{s}</option>)}
          </select> */}
        </div>
        <DataTable columns={columns} rows={filtered} loading={loading} emptyTitle="No tickets found" />
        <Pagination page={page} totalPages={totalPages} onChange={setPage} totalItems={totalCount ?? undefined} pageSize={PAGE_SIZE} />
      </Card>

      <SidePanel
        open={!!selected}
        onClose={() => { setSelected(null); setAdminReply(''); setInternalNotes(''); }}
        side="right"
        width={480}
        header={selected && (
          <div className="relative px-6 pt-5 pb-5" style={{ background: '#003c6c' }}>
            <button
              onClick={() => { setSelected(null); setAdminReply(''); setInternalNotes(''); }}
              className="absolute top-4 right-4 w-7 h-7 rounded-lg flex items-center justify-center text-white/60 hover:bg-white/10 hover:text-white"
            >
              <i className="ti ti-x" style={{ fontSize: 15 }} />
            </button>
            <div className="flex items-center gap-2 flex-wrap mb-3 pr-8">
              <Badge tone={statusToTone(selected.status)}>{selected.status}</Badge>
              <Badge tone={priorityTone(selected.priority)} dot={false}>{selected.priority}</Badge>
            </div>
            <h3 className="text-[17px] font-black text-white leading-snug pr-8">{selected.subject}</h3>
            <p className="text-[12px] text-white/50 font-medium mt-1">
              {selected.ticket_number} · {selected.name} {selected.email && <>· {selected.email}</>}
            </p>
          </div>
        )}
        footer={selected && activeTab === 'reply' && (
          <div className="border-t border-gray-100 px-6 py-4">
            <div className="flex gap-2 justify-end">
              <Button variant="secondary" size="sm" onClick={() => setSelected(null)}>Close</Button>
              <Button size="sm" onClick={handleResolve} loading={sending} disabled={selected.status === 'Closed'}>
                Send reply
              </Button>
            </div>
          </div>
        )}
      >
        {selected && (
          <div className="flex flex-col">
            {/* Original message */}
            <div className="px-6 py-5 border-b border-gray-100">
              <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest mb-2">
                Original message · {formatDateTime(selected.created_at)} · {selected.category || 'General'} · {selected.source || 'Website'}
              </p>
              <div className="bg-gray-50 border border-gray-100 rounded-xl p-4">
                <p className="text-[13px] text-gray-700 leading-relaxed whitespace-pre-wrap">{selected.message}</p>
              </div>
            </div>

            {/* Existing admin reply, if any */}
            {selected.admin_reply && (
              <div className="px-6 py-5 border-b border-gray-100">
                <p className="text-[10px] font-black text-green-600 uppercase tracking-widest mb-2">
                  Admin reply · {formatDateTime(selected.updated_at)}
                </p>
                <div className="bg-green-50 border border-green-100 rounded-xl p-4">
                  <p className="text-[13px] text-green-800 leading-relaxed whitespace-pre-wrap">{selected.admin_reply}</p>
                </div>
              </div>
            )}

            {/* Tabs */}
            <div className="flex border-b border-gray-100 px-6">
              {[
                { id: 'reply' as const, label: 'Reply', icon: 'ti-message-circle' },
                { id: 'notes' as const, label: 'Internal Notes', icon: 'ti-note' },
                { id: 'meta' as const, label: 'Category / Priority', icon: 'ti-adjustments' },
              ].map(tab => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center gap-1.5 text-[12px] font-bold px-3 py-3 border-b-2 -mb-px transition-colors ${activeTab === tab.id
                    ? 'border-navy text-navy'
                    : 'border-transparent text-gray-400 hover:text-gray-600'
                    }`}
                >
                  <i className={`ti ${tab.icon}`} style={{ fontSize: 14 }} />
                  {tab.label}
                </button>
              ))}
            </div>

            {/* Tab content */}
            <div className="p-6">
              {activeTab === 'reply' && (
                <div>
                  <label className="text-[11px] font-black text-gray-400 uppercase tracking-widest mb-2 block">
                    Reply to customer {selected.status === 'Closed' && <span className="text-amber-600">(ticket is closed)</span>}
                  </label>
                  <textarea
                    value={adminReply}
                    onChange={e => setAdminReply(e.target.value)}
                    rows={6}
                    disabled={detailLoading || selected.status === 'Closed'}
                    placeholder={selected.status === 'Closed' ? 'This ticket is closed.' : 'Write your response…'}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-[13px] text-gray-900 placeholder-gray-400 focus:outline-none focus:border-navy focus:ring-1 focus:ring-navy/20 transition-all resize-none disabled:bg-gray-50 disabled:text-gray-400"
                  />
                </div>
              )}

              {activeTab === 'notes' && (
                <div>
                  <label className="text-[11px] font-black text-gray-400 uppercase tracking-widest mb-2 block">
                    Internal notes <span className="text-gray-300 normal-case font-medium">(admin-only)</span>
                  </label>
                  <textarea
                    value={internalNotes}
                    onChange={e => setInternalNotes(e.target.value)}
                    rows={6}
                    disabled={detailLoading}
                    placeholder="Notes for the support team…"
                    className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-[13px] text-gray-900 placeholder-gray-400 focus:outline-none focus:border-navy focus:ring-1 focus:ring-navy/20 transition-all resize-none mb-4"
                  />
                  <div className="flex justify-end">
                    <Button size="sm" onClick={() => saveTicket({}, 'Internal notes saved.')} loading={sending}>Save notes</Button>
                  </div>
                </div>
              )}

              {activeTab === 'meta' && (
                <div className="flex flex-col gap-4">
                  <div>
                    <label className="text-[11px] font-black text-gray-400 uppercase tracking-widest mb-2 block">Status</label>
                    <select
                      value={statusDraft}
                      onChange={e => setStatusDraft(e.target.value)}
                      disabled={detailLoading}
                      className="w-full text-[13px] font-medium text-gray-700 bg-gray-50 border border-gray-200 rounded-lg px-3 py-2.5 focus:outline-none focus:border-navy"
                    >
                      {STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="text-[11px] font-black text-gray-400 uppercase tracking-widest mb-2 block">Category</label>
                    <select
                      value={categoryDraft}
                      onChange={e => setCategoryDraft(e.target.value)}
                      disabled={detailLoading}
                      className="w-full text-[13px] font-medium text-gray-700 bg-gray-50 border border-gray-200 rounded-lg px-3 py-2.5 focus:outline-none focus:border-navy"
                    >
                      {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="text-[11px] font-black text-gray-400 uppercase tracking-widest mb-2 block">Priority</label>
                    <select
                      value={priorityDraft}
                      onChange={e => setPriorityDraft(e.target.value)}
                      disabled={detailLoading}
                      className="w-full text-[13px] font-medium text-gray-700 bg-gray-50 border border-gray-200 rounded-lg px-3 py-2.5 focus:outline-none focus:border-navy"
                    >
                      <option value="Low">Low</option>
                      <option value="Medium">Medium</option>
                      <option value="High">High</option>
                    </select>
                  </div>
                  <div className="flex justify-end">
                    <Button size="sm" onClick={() => saveTicket({}, 'Ticket details saved.')} loading={sending}>Save changes</Button>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </SidePanel>
    </div>
  );
}




















